import { ApolloQueryResult, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { navigate } from 'gatsby';
import { complement, curry, equals, forEachObjIndexed } from 'ramda';
import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import StyledLink, { IStyledLinkProps } from '@ui/atoms/StyledLink';

import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import PhotoUpload from '@ui/organisms/PhotoUpload';

import aligned from '@ui/styled/Aligned';
import fullWidth from '@ui/styled/FullWidth';

import { uneditableFieldView } from '@helpers/Dashboard';

import { commonLabels } from '@utils/button';
import { ApiEndpoints } from '@utils/endpoints';
import { dropzoneErrors } from '@utils/error';
import { doesNotExist, exists } from '@utils/general';

import { getAuthToken } from '@providers/AuthProvider';

import { IPageProps } from '@folx-types/flow-steps';
import {
	IAccountInfoMutationData,
	IAccountInfoMutationResponse,
	IMemberData,
} from '@folx-types/member';

import ACCOUNT_INFO_MUTATION from '@graphql/mutations/AccountInfo.mutation.gql';

import { schema } from './LegalInfoEditStep.data';

export interface ILegalInfoEditStepPageContent {
	pageHeader: string;
	idLabel: string;
	ssnLabel: string;
	ssnDisclaimer: string;
	legalNameLabel: string;
	legalNameDisclaimer: string;
}

interface ILegalInfoEditStepProps extends IPageProps {
	pageContent: ILegalInfoEditStepPageContent;
	idImage?: string;
	member?: IMemberData;
	refetchLegalInfo?: () => void;
}

const AlignedLink = aligned<IStyledLinkProps>(StyledLink);
const FullWidthColumn = fullWidth(FlexColumn);
const FullWidthSubmitButton = fullWidth<ISubmitButtonProps>(SubmitButton);

const LegalInfoEditStep: FC<ILegalInfoEditStepProps> = ({
	idImage,
	member,
	pageContent,
	refetchLegalInfo,
}) => {
	const [showIdImageError] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const { pageHeader, idLabel, ssnLabel, ssnDisclaimer, legalNameLabel, legalNameDisclaimer } =
		pageContent;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {
			companyName: member?.companyName,
		},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const { handleSubmit, register, setValue, reset, resetField } = form;

	useDeepCompareEffect(() => {
		if (doesNotExist(member)) {
			return;
		}

		reset({
			companyName: member?.companyName,
			legalFirstName: member?.legalFirstName,
			legalLastName: member?.legalLastName,
		});
	}, [member]);

	useEffect(() => {
		if (doesNotExist(idImage)) {
			return;
		}

		resetField('idImage', { defaultValue: idImage });
	}, [idImage, resetField]);

	const [updateAccountInfo] = useMutation<IAccountInfoMutationResponse>(ACCOUNT_INFO_MUTATION);

	const updateIdImage = async (idImage: string) => {
		const blob = await (await fetch(idImage)).blob();

		const authorizationToken = await getAuthToken();

		try {
			await axios.put(`${ApiEndpoints.memberPortal}/image/idImage`, blob, {
				headers: {
					authorizationToken,
					'Content-Type': blob.type,
				},
			});
		} catch (error) {
			console.error(`Oof. We've run into a problem™ updating your ID. ${error}`);
			throw error;
		}
	};

	const updateMemberAccountInfo = async (input: IAccountInfoMutationData) => {
		try {
			await updateAccountInfo({
				variables: input,
			});
		} catch (error) {
			console.error(`Oof. We've run into a problem™ updating your information. ${error}`);
			throw error;
		}
	};

	const saveAccountInfo = async (formData: IAccountInfoMutationData['patch']) => {
		if (!member?.id) {
			throw new Error('Invalid user');
		}
		await updateMemberAccountInfo({
			clientMutationId: member?.id,
			memberId: member?.id,
			patch: formData,
		});
	};

	const onSubmit = async (data: Record<string, unknown>) => {
		const notEqual = curry((second) => complement(equals(second)));
		const hasNewIdImage = exists(data?.idImage) && notEqual(idImage)(data.idImage);
		let newData = {};

		forEachObjIndexed(async (value, key) => {
			if (key === 'idImage') {
				return;
			}
			const memberKey = member?.[key];
			const dataKey = data?.[key];
			const isNotEqual = notEqual(memberKey)(dataKey);
			newData = isNotEqual ? { ...newData, [key]: dataKey } : newData;
		})(data);

		let promises: Promise<ApolloQueryResult<IAccountInfoMutationResponse> | void>[] = [];
		promises = exists(newData) ? promises.concat(saveAccountInfo(newData)) : promises;
		promises = hasNewIdImage ? promises.concat(updateIdImage(data.idImage as string)) : promises;

		if (doesNotExist(promises)) {
			return;
		}

		setSubmitting(true);
		try {
			await Promise.all(promises);
		} catch (error) {
			console.log(error);
			// setAccountError(error);
			return;
		} finally {
			setSubmitting(false);
			refetchLegalInfo?.();
		}

		navigate('..');
	};

	const prepareSSN = (ssn: string) => `***-**-${ssn.split('-')[2]}`;
	const ssnExists = exists(member?.ssn) && member?.ssn !== '000-00-0000';

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FullWidthColumn gap="lg">
				<HeadingText>{pageHeader}</HeadingText>
				<FullWidthColumn gap="sm">
					<BasicText variant="field">{idLabel}</BasicText>
					<PhotoUpload
						imageType="Photo ID"
						variant="row"
						previousUpload={idImage}
						hideDelete
						handleUpload={(upload) => {
							setValue('idImage', upload, { shouldValidate: true });
						}}>
						{showIdImageError && (
							<BasicText color="red" tint="darken40">
								{dropzoneErrors.required}
							</BasicText>
						)}
					</PhotoUpload>
					<input type="hidden" {...register('idImage')} />
				</FullWidthColumn>
				<FullWidthColumn gap="sm">
					{uneditableFieldView(
						ssnLabel,
						ssnDisclaimer,
						ssnExists ? prepareSSN(member?.ssn ?? '') : undefined
					)}
				</FullWidthColumn>
				<FullWidthColumn gap="sm">
					{uneditableFieldView(
						legalNameLabel,
						legalNameDisclaimer,
						member?.legalFirstName || member?.legalLastName
							? `${member?.legalFirstName ?? ''} ${member?.legalLastName ?? ''}`
							: undefined
					)}
				</FullWidthColumn>
				<FullWidthSubmitButton
					label={commonLabels.save}
					variant="secondary"
					submitting={submitting}
				/>
				<AlignedLink alignSelf="center" variant="underline" to="..">
					{commonLabels.cancel}
				</AlignedLink>
			</FullWidthColumn>
		</form>
	);
};

export default LegalInfoEditStep;
